/**
 * HEADER COMPONENT - LANDING PAGE NAVIGATION
 * ==========================================
 * 
 * The main navigation header for the public-facing landing page.
 * This appears before users sign in and provides navigation to different
 * sections of the landing page plus sign-in/sign-up actions.
 * 
 * LAYOUT STRUCTURE:
 * - Logo: Left-aligned (now using custom brand logo)
 * - Navigation: Center-aligned
 * - Theme toggle + Login items: Right-aligned
 * 
 * FEATURES:
 * - Responsive design (mobile-friendly)
 * - Smooth scroll navigation to page sections
 * - Sign-in and sign-up call-to-action buttons
 * - Dark/light theme toggle
 * - Sticky positioning with backdrop blur
 * - Three-column layout for proper alignment
 * - Custom brand logo integration
 */

import { useState } from "react";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { Logo } from "./Logo";

interface HeaderProps {
  onSignIn: () => void;              // Navigate to sign-in page
  onSignUp: () => void;              // Navigate to sign-up page  
  onScrollToSection: (sectionId: string) => void; // Smooth scroll to sections
  onOpenDemo: () => void;            // Open demo video popup
}

export function Header({ onSignIn, onSignUp, onScrollToSection, onOpenDemo }: HeaderProps) {
  // State for mobile menu visibility
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Toggle mobile menu open/closed
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Handle navigation with mobile menu close
  const handleNavigation = (sectionId: string) => {
    onScrollToSection(sectionId);
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  // Handle logo click - scroll to top of page
  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center">
        
        {/* THREE-COLUMN LAYOUT FOR DESKTOP */}
        {/* Column 1: LOGO (Left-aligned) */}
        <div className="flex-shrink-0">
          <Logo 
            size="md" 
            onClick={handleLogoClick}
            showText={true}
          />
        </div>

        {/* Column 2: NAVIGATION (Center-aligned) - Hidden on mobile */}
        <nav className="hidden md:flex items-center justify-center flex-1 space-x-8">
          <button 
            onClick={() => handleNavigation('features')}
            className="text-foreground/80 hover:text-foreground transition-colors"
          >
            Features
          </button>
          <button 
            onClick={() => handleNavigation('pricing')}
            className="text-foreground/80 hover:text-foreground transition-colors"
          >
            Pricing
          </button>
          <button 
            onClick={onOpenDemo}
            className="text-foreground/80 hover:text-foreground transition-colors"
          >
            Demo
          </button>
          <button 
            onClick={() => handleNavigation('testimonials')}
            className="text-foreground/80 hover:text-foreground transition-colors"
          >
            Testimonials
          </button>
        </nav>

        {/* Column 3: ACTIONS (Right-aligned) - Hidden on mobile */}
        <div className="hidden md:flex items-center space-x-4 flex-shrink-0">
          <ThemeToggle />
          <Button variant="ghost" onClick={onSignIn}>
            Sign In
          </Button>
          <Button onClick={onSignUp}>
            Get Started
          </Button>
        </div>

        {/* MOBILE: SPACER + THEME TOGGLE + MENU BUTTON */}
        <div className="flex md:hidden items-center space-x-2 ml-auto">
          <ThemeToggle />
          <Button 
            variant="ghost" 
            size="icon"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </Button>
        </div>
      </div>

      {/* MOBILE MENU DROPDOWN - Only visible when menu is open */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t bg-background/95 backdrop-blur">
          <div className="container py-4 space-y-4">
            
            {/* Mobile Navigation Links */}
            <div className="space-y-2">
              <button 
                onClick={() => handleNavigation('features')}
                className="block w-full text-left px-4 py-2 text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-md transition-colors"
              >
                Features
              </button>
              <button 
                onClick={() => handleNavigation('pricing')}
                className="block w-full text-left px-4 py-2 text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-md transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => {
                  onOpenDemo();
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-md transition-colors"
              >
                Demo
              </button>
              <button 
                onClick={() => handleNavigation('testimonials')}
                className="block w-full text-left px-4 py-2 text-foreground/80 hover:text-foreground hover:bg-accent/50 rounded-md transition-colors"
              >
                Testimonials
              </button>
            </div>

            {/* Mobile Action Buttons */}
            <div className="pt-4 border-t space-y-2">
              <Button 
                variant="ghost" 
                className="w-full justify-start" 
                onClick={() => {
                  onSignIn();
                  setIsMobileMenuOpen(false);
                }}
              >
                Sign In
              </Button>
              <Button 
                className="w-full" 
                onClick={() => {
                  onSignUp();
                  setIsMobileMenuOpen(false);
                }}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}